import { Box } from '@mui/material';
import { ReportCard } from 'elements/ReportCard';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { readStoredUser } from 'utils/authSession';
import {
  canRenderUserCount,
  getPropertyUserCardPath,
  resolveAdminLandlordCount,
} from './userCountNavigation';
import { roleUserCountTileLabel } from 'utils/entityTerminology';

const countRowsByRoleField = (rows, field) => {
  const counts = { ll: 0, vp: 0, rm: 0, pm: 0 };

  (rows || []).forEach((row) => {
    const bucketRole = row?.[field];
    if (bucketRole && Object.prototype.hasOwnProperty.call(counts, bucketRole)) {
      counts[bucketRole] += 1;
    }
  });

  return counts;
};

const formatCount = (value) => Number(value ?? 0).toLocaleString();

export const UserCount = ({
  data = [],
  viewerRole,
  landlordCount,
  activeLandlordCount,
  vpDelegationTotal,
  rmDelegationTotal,
  pmDelegationTotal,
}) => {
  const navigate = useNavigate();

  const role = viewerRole ?? readStoredUser()?.role;

  const counts = useMemo(() => {
    const roleCounts = countRowsByRoleField(data, role === 'admin' ? 'role' : 'accepter_role');

    if (role !== 'admin') {
      return roleCounts;
    }

    return {
      ll: resolveAdminLandlordCount({
        landlordCount,
        activeLandlordCount,
        userTableCount: roleCounts.ll,
      }),
      vp: vpDelegationTotal ?? roleCounts.vp,
      rm: rmDelegationTotal ?? roleCounts.rm,
      pm: pmDelegationTotal ?? roleCounts.pm,
    };
  }, [
    data,
    role,
    landlordCount,
    activeLandlordCount,
    vpDelegationTotal,
    rmDelegationTotal,
    pmDelegationTotal,
  ]);

  if (!canRenderUserCount(role)) {
    return null;
  }

  const propertyUserListPath = getPropertyUserCardPath(role);

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      gap="20px"
      sx={{ width: '100%', minWidth: 0, alignItems: 'stretch' }}
    >
      {role === 'admin' && (
        <Box sx={{ flex: '1 1 200px', minWidth: 0, displayItems: 'stretch' }}>
          <ReportCard
            title={roleUserCountTileLabel('ll')}
            count={formatCount(counts.ll)}
            onClick={() => navigate('/user/operator')}
          />
        </Box>
      )}

      {(role === 'admin' || role === 'll') && (
        <Box sx={{ flex: '1 1 200px', minWidth: 0, displayItems: 'stretch' }}>
          <ReportCard
            title={roleUserCountTileLabel('vp')}
            count={formatCount(counts.vp)}
            onClick={() => navigate('/user/full-portfolio')}
          />
        </Box>
      )}

      {(role === 'admin' || role === 'll' || role === 'vp') && (
        <Box sx={{ flex: '1 1 200px', minWidth: 0, displayItems: 'stretch' }}>
          <ReportCard
            title={roleUserCountTileLabel('rm')}
            count={formatCount(counts.rm)}
            onClick={() => navigate('/user/multi-site')}
          />
        </Box>
      )}

      <Box sx={{ flex: '1 1 200px', minWidth: 0, displayItems: 'stretch' }}>
        <ReportCard
          title={roleUserCountTileLabel('pm')}
          count={formatCount(counts.pm)}
          onClick={() => navigate(propertyUserListPath)}
        />
      </Box>
    </Box>
  );
};
